import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigateWithSearch } from '../../../common/hooks/useNavigateWithSearch';
import { BookingsService } from '../../../api/BookingsService';
import { PriceService } from '../../../api/PriceService';
import { catchCallback } from '../../../api/request';
import { PageTitle } from '../../../common/components/PageTitle';
import { useDictionary } from '../../../common/contexts/DictionaryContext';
import { findLang } from '../../../common/models/lang';
import { checkShortTermBooking } from '../../../common/utils/checkShortTermBooking';
import { minBookingDate } from '../../../common/utils/date';
import { nowInGermany } from '../../../common/utils/serverClock';
import { ONLINE_PAYMENT_TYPES, PAYMENT_INVOICE } from '../../../constants';
import { useLoyalty } from '../../../loyalty/LoyaltyContext';
import { scrollToFirstError } from '../../utils/validationUx';
import { bookingClean } from '../BookingPage/bookingClean';
import { currentTimeRelated } from '../BookingPage/bookingReducer';
import { TRANSFER } from '../../models/transfer';
import { BookingSummary } from '../BookingSummary';
import { WithSidebar } from '../WithSidebar';
import css from '../BookingDetailsForm/BookingDetailsForm.module.css';
import { Company } from './Company';
import { EmailSubscription } from './EmailSubscription';
import { InvoiceTo } from './InvoiceTo';
import { PaymentPart } from './PaymentPart';
import { Terms } from './Terms';
import { TotalPrice } from './TotalPrice';
import { UserAddress } from './UserAddress';
import { UserContacts } from './UserContacts';
import { UserName } from './UserName';
import { CreateAccount } from './CreateAccount';
import { logAmplitudeEvent } from '../../../common/services/amplitudeService';

const BookingPaymentForm = ({
    data: { booking, firstTransfer, secondTransfer },
    update: { updateBooking, tickBooking, resetBooking },
    url,
    touch: { touchBooking },
    errorsSrc: { bookingErrorSrc },
    validationErrors,
    showValidationErrors,
    notifications,
    user,
    setUser,
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigateWithSearch();
    const { languages = [] } = useDictionary();
    const onChange = updateBooking;
    const isSmDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));

    const { loyalty, refresh } = useLoyalty();
    const [quoteLoading, setQuoteLoading] = useState(false);

    const onNumberChange = ({ target, name, value }) => {
        if (target) {
            ({ name, value } = target);
        }
        updateBooking({ value: parseInt(value) || '', name });
    };

    const onCheckboxChange = (e, value) => {
        const { name } = e.target;
        updateBooking({ value, name });
    };

    const onBook = (e) => {
        e.stopPropagation();
        e.preventDefault();

        // Freshness guard: the surcharge in state and the rendered validation are at most
        // one tick (60 s) old. If 12:00 or midnight passed since, re-tick instead of booking;
        // the re-render shows the new surcharge (and reloads the quote) or the date error,
        // and the customer clicks again.
        const minDate = minBookingDate();
        const hasPastDate = booking.transfers.some((transfer) => {
            const date = transfer.typeId === TRANSFER.SPECIAL ? transfer.tripDate : transfer.flight?.date;
            return date && date < minDate;
        });
        if (currentTimeRelated(booking) !== booking || hasPastDate) {
            tickBooking();
            return;
        }

        // Hard guard (spec §Decided 4): the button is always enabled, so the booking
        // API call must never fire with invalid data.
        if (validationErrors) {
            showValidationErrors(validationErrors);
            scrollToFirstError();
            return;
        }

        updateBooking({ name: 'busy', value: true });
        notifications.clearError();

        const bData = {
            ...bookingClean(booking),
            orderDate: nowInGermany().toISODate(),
            orderTime: nowInGermany().toFormat('T') + ':00',
            languageId: languages.find(({ name }) => name === findLang(i18n.language).name).id,
            autoCalculation: Boolean(booking.price),
            ...(booking.promoCodeId && { promoCodeId: booking.promoCodeId }),
            ...(booking.redeemLoyaltyCredit && { redeemLoyaltyCredit: true }),
        };

        // Logged-in users must never register a new account through this flow — the
        // create-account checkbox is hidden while logged in (see createAccountComponent
        // below), and this is belt-and-suspenders so points/attribution always stay on
        // the existing, already-authenticated account (PR6 Step 2, SoT §13).
        if (user) {
            bData.user = { ...bData.user, register: false };
        }

        // Placing a booking accepts the current AGB incl. the loyalty terms (see Terms.jsx),
        // so a registering guest has consented; the BE stamps agbAcceptedAt/agbVersion from this.
        bData.user = { ...bData.user, agbConsent: Boolean(bData.user.register) };

        BookingsService.create(bData)
            .then(async (response) => {
                // Test to log amplitude event
                logAmplitudeEvent('Booking created', {
                    bookingNumber: response.bookingNumber,
                });

                // "Create account" checkbox was ticked. Like the modal's request event this also
                // counts emails that already have an account (the BE then only sends a reminder).
                if (bData.user.register) {
                    logAmplitudeEvent('Signup: Requested', { method: 'booking_checkbox' });
                }

                // Log the amount the SERVER actually applied (SoT §15 "Bonusguthaben
                // redeemed volume"), not the balance fetched at page load — the server
                // clamps to the booking total and declines outright when the tier or
                // price gate fails.
                if (booking.redeemLoyaltyCredit && response.loyaltyRedeemedCents > 0) {
                    logAmplitudeEvent('Loyalty credit redeemed', {
                        amountCents: response.loyaltyRedeemedCents,
                    });
                }

                // Points/credit changed server-side — refresh so the Header chip and any later
                // loyalty view update without a reload. Fire-and-forget; guests: refresh() no-ops
                // anyway, but the `if (user)` keeps intent explicit.
                if (user) refresh();

                let paymentLink;

                // check if the payment type is "online" and the price has been generated and not short-term
                // if so, generate a payment link
                const isBookingPaidOnline = ONLINE_PAYMENT_TYPES.includes(booking.paymentTypeId);
                const isBookingWithPrice = response.transfers[0].price;
                const isBookingShortTerm = checkShortTermBooking(response.transfers);
                // response.payment is NOT a usable "nothing to charge" signal here: at create
                // time every transfer is OPEN (never CONFIRMED), so buildInitialPayment always
                // returns null and the Payment row is only created lazily by getCheckoutLink —
                // response.payment is absent for every booking, redeemed or not. Instead, derive
                // the payable total in integer cents (never a float === 0 comparison) and
                // subtract the redeemed cents the server already computed and returned on the
                // booking: <= 0 means there is nothing left to charge.
                const transfersTotalCents = response.transfers.reduce(
                    (sum, tr) => sum + Math.round((tr.price?.totalPrice || 0) * 100),
                    0,
                );
                const isFullyRedeemed = transfersTotalCents - (response.loyaltyRedeemedCents || 0) <= 0;

                if (isBookingPaidOnline && isBookingWithPrice && !isBookingShortTerm && !isFullyRedeemed) {
                    const checkoutLinkResponse = await BookingsService.getCheckoutLink(response.bookingNumber);
                    paymentLink = checkoutLinkResponse.paymentLink;

                    // Log Unzer customer creation errors to Amplitude if present
                    if (checkoutLinkResponse.unzerCustomerErrors?.length > 0) {
                        checkoutLinkResponse.unzerCustomerErrors.forEach((error) => {
                            logAmplitudeEvent('Unzer: Customer Creation Failed', {
                                bookingNumber: response.bookingNumber,
                                errorCode: error.code,
                                merchantMessage: error.merchantMessage,
                                customerMessage: error.customerMessage,
                                errorType: 'unzer_customer_creation',
                            });
                        });
                    }
                }

                // some maintenance
                if (user && bData.user.saveToUserProfile) {
                    setUser({
                        ...user,
                        ...bData.user,
                        saveToUserProfile: false,
                        address: {
                            ...user.address,
                            ...bData.user.address,
                        },
                    });
                }

                // if we have a payment link, send the user to the payment page
                if (paymentLink) {
                    navigate(`${url}/success`, {
                        state: { paymentLink },
                    });
                } else {
                    sessionStorage.setItem('thankYouHasPriceCalculated', Boolean(isBookingWithPrice));
                    navigate(`${url}/thank-you`, {
                        state: { hasPriceCalculated: Boolean(isBookingWithPrice) },
                    });
                    resetBooking();
                }
            })
            .catch((error) => {
                updateBooking({ name: 'busy', value: false });
                notifications.showError(error.response?.data);
            });
    };

    // Method to extract address pairs per transfer for logging
    const extractTransferAddressPairs = (booking) => {
        return booking.transfers.map((transfer, index) => ({
            transferIndex: index,
            addressFrom: transfer.addressFrom?.gPlaceId ? transfer.addressFrom : null,
            placeIdFrom: transfer.addressFrom?.gPlaceId ?? null,
            addressTo: transfer.addressTo?.gPlaceId ? transfer.addressTo : null,
            placeIdTo: transfer.addressTo?.gPlaceId ?? null,
        }));
    };

    const loadPrice = () => {
        const request = PriceService.calculatePrice(bookingClean(booking));

        // quoteLoading disables the Book button: a click cannot submit against a quote still
        // loading. Local state, not booking.busy: the reducer drops every other action while
        // busy (the price would be lost) and booking state outlives this page. A cancelled
        // request (transfers changed again) must not clear the flag the newer request has set.
        let cancelled = false;
        setQuoteLoading(true);
        request
            .then((data) => {
                updateBooking({ name: 'price', value: data });
            })
            .finally(() => !cancelled && setQuoteLoading(false))
            .catch(
                catchCallback(() => {
                    updateBooking({ name: 'price', value: null });
                    // if no price could be calculated, log to Amplitude with full address details

                    const transferAddressPairs = extractTransferAddressPairs(booking);

                    transferAddressPairs.forEach(
                        ({ transferIndex, addressFrom, placeIdFrom, addressTo, placeIdTo }) => {
                            logAmplitudeEvent('Place ID: Price Calculation Failed', {
                                placeIdFrom,
                                addressFrom,
                                placeIdTo,
                                addressTo,
                                transferNumber: transferIndex,
                                errorType: 'price_calculation_failed',
                            });
                        },
                    );
                }),
            );

        return () => {
            cancelled = true;
            request.cancel();
        };
    };

    useEffect(loadPrice, [booking.transfers, booking.paymentTypeId, booking.promoCodeId]);

    const bookingError = bookingErrorSrc.bind(null, validationErrors);

    const paymentMethodsComponent = booking.price ? (
        <PaymentPart
            booking={booking}
            onNumberChange={onNumberChange}
            user={user}
            variant={isSmDown ? 'body2' : 'body1'}
        />
    ) : null;

    const companyFields = (
        <>
            {/* company fields are needed only for invoice payment */}
            {booking.paymentTypeId === PAYMENT_INVOICE && !!user && (
                <Company
                    booking={booking}
                    bookingError={bookingError}
                    touchBooking={touchBooking}
                    onChange={onChange}
                    sx={{ ml: 3 }}
                />
            )}
        </>
    );

    const emailSubscriptionComponent = (
        <EmailSubscription
            booking={booking}
            onCheckboxChange={onCheckboxChange}
            variant={isSmDown ? 'body2' : 'body1'}
            sx={{ mt: 1.5, ml: 0.5 }}
        />
    );

    const createAccountComponent = (
        <CreateAccount
            booking={booking}
            onCheckboxChange={onCheckboxChange}
            variant={isSmDown ? 'body2' : 'body1'}
            sx={{ ml: 0.5 }}
        />
    );

    const submitButton = (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3.5, mb: 2 }}>
            <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                disabled={booking.busy || quoteLoading}
                onClick={onBook}
            >
                {t(booking.price ? 'booking-action-proceed' : 'booking-action-request')}
            </Button>
            {(booking.busy || quoteLoading) && <CircularProgress size={24} className={css.buttonProgress} />}
        </Box>
    );

    const terms = <Terms />;

    const content = (
        <form noValidate className={css.host} onSubmit={onBook}>
            <Typography variant="h4" sx={{ mb: 2 }}>
                {t('billing-address')}
            </Typography>

            <InvoiceTo
                booking={booking}
                onNumberChange={onNumberChange}
                onChange={onChange}
                bookingError={bookingError}
                touchBooking={touchBooking}
            />

            <UserName
                booking={booking}
                onChange={onChange}
                onCheckboxChange={onCheckboxChange}
                onNumberChange={onNumberChange}
                bookingError={bookingError}
                touchBooking={touchBooking}
            />

            <UserAddress
                booking={booking}
                onChange={onChange}
                bookingError={bookingError}
                touchBooking={touchBooking}
            />

            <Divider sx={{ mt: 1.5, mb: 2.5 }} />

            <UserContacts
                booking={booking}
                onChange={onChange}
                bookingError={bookingError}
                touchBooking={touchBooking}
            />

            {user && (
                <div className={css.editUserRow}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={booking.user.saveToUserProfile}
                                onChange={onCheckboxChange}
                                name="user.saveToUserProfile"
                            />
                        }
                        label={<Typography>{t('saveToUserProfile')}</Typography>}
                    />
                </div>
            )}

            <div className={css.bottomBox}>
                {paymentMethodsComponent}
                {companyFields}
                {emailSubscriptionComponent}
                {!user && createAccountComponent}
                {submitButton}
                {terms}
            </div>
        </form>
    );

    const sidebar = (
        <>
            <div className={css.sidebarBottomBox}>{paymentMethodsComponent}</div>
            <Stack sx={{ flexDirection: { xs: 'column', sm: 'row', md: 'column' }, gap: { xs: 3, sm: 1.5, md: 3 } }}>
                <BookingSummary firstTransfer={firstTransfer} secondTransfer={secondTransfer} url={url} />
                <TotalPrice
                    price={booking.price}
                    serviceTypeId={booking.transfers[0].serviceTypeId}
                    onChange={onChange}
                    loyalty={loyalty}
                    redeem={booking.redeemLoyaltyCredit}
                    companyId={booking.company?.id}
                    agencyId={booking.agencyId}
                />
            </Stack>
            <div className={css.sidebarBottomBox}>
                {companyFields}
                {emailSubscriptionComponent}
                {!user && createAccountComponent}
                {submitButton}
                {terms}
            </div>
        </>
    );

    return (
        <>
            <PageTitle>{t('booking.navigation.payment')}</PageTitle>
            <Box sx={(theme) => theme.mixins.box(theme)}>
                <WithSidebar content={content} sidebar={sidebar} />
            </Box>
        </>
    );
};

export { BookingPaymentForm };
